import type { FieldError, FieldPath, RegisterOptions } from 'react-hook-form';
import { isAddress } from 'viem/utils';
import type { FormDataUnion } from './helpers';

export const MAX_CHAR_LENGTH = {
  tokenSymbol: 8,
  description: 280,
  behindTheMusic: 6000,
  manuallyAddedDescription: 144,
  rewardTitle: 60,
  rewardDescription: 300,
  lyrics: 6000,
} as const;

const noValidation = {
  rules: { required: false },
  errorMessages: {},
};

function validationRules<
  T extends Record<
    string,
    {
      rules: RegisterOptions;
      errorMessages: {
        [rule in keyof RegisterOptions]: string;
      };
    }
  >,
>(v: T) {
  return v;
}

const VALIDATION_RULES = validationRules({
  releaseType: {
    rules: { required: true },
    errorMessages: { required: 'Release Type is required' },
  },
  title: {
    rules: { required: true, pattern: /^.*[a-zA-Z0-9&$]+.*$/ },
    errorMessages: {
      required: 'Song Title is required',
      pattern: 'Title must contain at least one letter or number',
    },
  },
  tokenSymbol: {
    rules: { required: true, maxLength: MAX_CHAR_LENGTH['tokenSymbol'], pattern: /^[A-Z0-9]+$/ },
    errorMessages: {
      required: 'Token Symbol is required',
      maxLength: 'Token Symbol must be fewer than 8 characters',
      pattern: 'Token Symbol must be all capital letters or numbers',
    },
  },
  genre: {
    rules: { required: true },
    errorMessages: { required: 'Genre is required ' },
  },
  behindTheMusic: {
    rules: { required: true, maxLength: MAX_CHAR_LENGTH['behindTheMusic'] },
    errorMessages: {
      required: 'Behind The Music is required',
      maxLength: 'Behind The Music must be fewer than 6000 characters',
    },
  },
  coverImage: {
    rules: { required: true },
    errorMessages: {
      required: 'An image file is required',
    },
  },
  songFile: {
    rules: { required: true },
    errorMessages: {
      required: 'An audio file is required',
    },
  },
  key: noValidation,
  lyrics: noValidation,
  license: noValidation,
  location: noValidation,
  tempCoverImage: noValidation,
  staticCoverImage: noValidation,
  presaleAllowList: {
    rules: { required: true, minLength: 1 },
    errorMessages: {
      required: 'Presale Allowlist is required',
      minLength: 'At least one address must be added',
    },
  },
  role: {
    rules: { required: true },
    errorMessages: { required: 'Please provide a role for each owner' },
  },
  ownership: {
    rules: { required: true },
    errorMessages: { required: 'Please provide a percentage for each owner' },
  },
  albumMintSupply: {
    rules: { required: true },
    errorMessages: { required: 'Album mint supply is required' },
  },
  allowListAddress: {
    rules: { validate: val => (val === '' ? true : isAddress(val)) },
    errorMessages: { validate: 'Please enter a valid Ethereum address' },
  },
  deployCheckBox: {
    rules: { required: true },
    errorMessages: { required: 'Checkbox must be selected in order to deploy' },
  },
  chainId: {
    rules: { required: true },
    errorMessages: { required: 'Select a network to deploy your contracts' },
  },
  manuallyAddedDescription: {
    rules: { maxLength: MAX_CHAR_LENGTH['manuallyAddedDescription'] },
    errorMessages: {
      maxLength: `Description must be fewer than ${MAX_CHAR_LENGTH['manuallyAddedDescription']} characters`,
    },
  },
});

type UploadToolFieldName = keyof typeof VALIDATION_RULES;

export const getValidationRules = (name: UploadToolFieldName | null) => {
  if (!name) return {};

  return VALIDATION_RULES[name].rules;
};

export const getErrorMessage = (
  name: UploadToolFieldName | FieldPath<FormDataUnion> | null,
  error?: FieldError,
): string => {
  if (!error || !name) return '';

  const validationRule = VALIDATION_RULES[name as UploadToolFieldName];

  return (
    validationRule?.errorMessages[error.type as keyof typeof validationRule.errorMessages] ?? ''
  );
};
